import React, { Suspense, useState, useEffect } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { Outlet, useLocation } from 'react-router-dom' // renders child components wherever this element exists, allows Navbar/Sidebar to be on every page
import { useSelector } from 'react-redux'
import './Layout.css'

import Navbar from "components/Navbar"
import { CrmContextProvider } from "components/CrmContext"
import PersonalWorkspace from "components/PersonalWorkspace"
import Sidebar from "components/Sidebar"
import FbTokenExpiryBanner from "components/FbTokenExpiryBanner"
import ImpersonationBanner from "components/ImpersonationBanner"
import InstallPrompt from "pwa/InstallPrompt"
import NotificationGate from "pwa/NotificationGate"
import CrmIntegrationsAlert from "components/CrmIntegrationsAlert"
import BootSplash from "components/BootSplash"
import BottomNav from "components/BottomNav"
import LoadingGate from "components/LoadingGate"

const DRAWER_WIDTH = "220px"

function Layout() {
  const theme = useTheme()
  const isNonMobile = useMediaQuery("(min-width: 600px)") // returns boolean to determine if app is mobile
  const [isSidebarOpen, setIsSidebarOpen] = useState(isNonMobile)
  const location = useLocation()
  const user = useSelector((s) => s.auth.user)

  // Role-aware browser tab title so multi-tab users can tell sessions apart.
  useEffect(() => {
    const prefix = user?.role ? `${user.role} · ` : ""
    document.title = `${prefix}Phieg CRM`
  }, [user?.role])

  const transition = theme.transitions.create(["margin", "width"], {
    easing: isSidebarOpen
      ? theme.transitions.easing.easeOut
      : theme.transitions.easing.sharp,
    duration: isSidebarOpen
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  })

  return (
    <CrmContextProvider>
    <Box
      display={isNonMobile ? "flex" : "block"}
      width="100%"
      height="100%"
      // overflow-x: clip clips like hidden but does NOT create a scroll
      // container, so position:sticky children (the mobile app bar) still pin
      // to the viewport. hidden stays as the fallback for older engines.
      sx={{
        overflowX: "hidden",
        "@supports (overflow-x: clip)": { overflowX: "clip" },
      }}
    >
      <BootSplash />
      <Sidebar
        isNonMobile={isNonMobile}
        drawerWidth={DRAWER_WIDTH}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <Box
        flexGrow={1}
        sx={{
          minWidth: 0,
          width: "100%",
          overflowX: "hidden",
          "@supports (overflow-x: clip)": { overflowX: "clip" },
          transition,
          marginLeft:
            isNonMobile && !isSidebarOpen ? `-${DRAWER_WIDTH}` : 0,
          // Clear the fixed bottom nav on mobile so it never covers content.
          paddingBottom: isNonMobile
            ? 0
            : "calc(56px + var(--safe-area-bottom, 0px))",
        }}
      >
        {/* On phones the banner + app bar pin to the top as one unit (a
            single sticky wrapper — two sibling stickies would overlap).
            Desktop keeps the in-flow static header. */}
        <Box
          sx={{
            position: { xs: "sticky", sm: "static" },
            top: 0,
            zIndex: (t) => t.zIndex.appBar,
          }}
        >
          <ImpersonationBanner />
          <Navbar
            isNonMobile={isNonMobile}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        </Box>
        <FbTokenExpiryBanner />
        <InstallPrompt />
        {/* First-open "turn on notifications" bottom sheet — one tap
            subscribes on the spot. Shows at most once a day, only while the
            browser has never been asked. */}
        <NotificationGate />
        <CrmIntegrationsAlert />
        <div key={location.pathname} className="route-fade">
          {/* Pages are lazy chunks (route-level code splitting in App.js);
              this boundary keeps the chrome (Navbar/Sidebar/BottomNav) on
              screen while a chunk downloads — only the page area gates. */}
          <Suspense fallback={<LoadingGate loading minHeight={360} />}>
            <Outlet context={isSidebarOpen} />
          </Suspense>
        </div>
      </Box>
      {!isNonMobile && <BottomNav />}
      {/* Floating personal workspace — mounted once here so it is present on
          every CRM view without any page knowing about it. Renders nothing for
          roles that do not have it. */}
      <PersonalWorkspace />
    </Box>
    </CrmContextProvider>
  )
}

export default Layout